import type { ApiEnvelope, PublicSitePage, SiteNewsCard, SiteNewsListResponse } from '~/types/types'

/** Public tenant-site content — pages + news (tenant resolved from host). */
export const usePublicSiteRepository = () => {
  const api = useApi()

  const getPage = (slug: string) => {
    return api.get<ApiEnvelope<PublicSitePage>>(`/api/public/site/pages/${slug}`)
  }

  /**
   * The tenant's published rate card (self-provided, active offerings only).
   * Backs the `services_list` block.
   */
  const getPriceList = (query: { category?: string, limit?: number } = {}) => {
    return api.get<{ data: PublicPriceList }>('/api/public/price-list', { query })
  }

  const getNews = (query: { tab?: 'latest' | 'popular', category?: string, page?: number, per_page?: number } = {}) => {
    return api.get<SiteNewsListResponse>('/api/public/site/news', { query })
  }

  const getArticle = (slug: string) => {
    return api.get<{ data: PublicSitePage, more_from: SiteNewsCard[] }>(`/api/public/site/news/${slug}`)
  }

  const trackView = (slug: string) => {
    return api.post<{ message: string }>(`/api/public/site/news/${slug}/view`)
  }

  return {
    getPage,
    getNews,
    getArticle,
    trackView,
    getPriceList
  }
}

export interface PublicPriceListItem {
  id: number
  name: string
  note: string | null
  unit: string
  price_amount: number | null
  price_max: number | null
  currency: string | null
  is_band: boolean
  is_quote_only: boolean
}

export interface PublicPriceListModifierOption {
  key: string
  label: string
  kind: 'percent' | 'fixed'
  rate: number
  amount: number
  is_default: boolean
  /** true when this option would not change the price — the shipped state. */
  is_free: boolean
}

export interface PublicPriceList {
  currency: string
  groups: Array<{ category: string | null, items: PublicPriceListItem[] }>
  modifiers: Array<{ group: string, options: PublicPriceListModifierOption[] }>
}
